import {
  AppointmentStatusEnum,
  SlotStatusEnum,
} from "@frontend/lib/graphql/generated-gql";
import type {
  AdditionalInsuranceMode,
  AppointmentSchema,
  BoughtTreatmentSchema,
  ProcedureSchema,
  SlotsSchema,
} from "@frontend/lib/schemas/appointmentSchema";
import { InsuranceMode } from "@frontend/lib/schemas/clientSchema";
import type { StateCreator } from "zustand";
import type { Store } from "../globalStore";

// Inlined to avoid circular dependency with helper.ts
const getProcedureClientStatus = (
  status: "NEW" | "EDITING" | "DELETED" | undefined,
) => {
  if (status === "DELETED") {
    return "NEW" as const;
  }
  if (status === "NEW") {
    return "DELETED" as const;
  }

  return "DELETED" as const;
};

export type queryIdType = "userId" | "consumerId" | "insuranceId";
export type PaymentIntent = {
  paymentIntentId: string;
  clientSecret: string;
  customerOptions?: { customer: string; ephemeralKey: string };
  /**
   * PLA-2474. Set when the intent was minted by A2 against a payment
   * contract. Its presence switches finalize from the legacy shape (amount
   * and BT ids decided in the browser) to `finalize(contractId)`, where the
   * server reads both from the frozen contract.
   */
  paymentContractId?: string;
  /**
   * Pen Test Issue #1. The amount, in cents, the intent was created with when
   * it came from hex-pc's quote rather than the client total. Capture is
   * checked against it, and the PI metadata reports it.
   */
  authoritativeAmountCents?: number;
  /**
   * Shadow mode only: hex-pc's quote for the cart, in cents, fetched for
   * logging while the card is authorized for the displayed total.
   */
  shadowQuotedAmountCents?: number;
};

export const initialVisitInfo = {
  appointmentStart: "",
  appointmentEnd: "",
  practiceId: 0,
  providerId: undefined,
  ehrVisitIds: undefined,
};

export type AppointmentStatusObject = {
  [id: string]: {
    isUpdated: boolean;
    updatedSlots: string[];
    reason: string;
  };
};

/**
 * !IMPORTANT!
 * This store is used to store appointment / slots information as it is getting built on the form
 * NOT IN PARITY WITH ACTUAL DATA MODEL!!
 */
export interface AppointmentSlice {
  appointments: AppointmentSchema[]; // list of appointments that serve as source of truth that you see at the end of a form session
  globalDotRelatedConsumerId: string | null; // todo: get rid of this after refactor
  globalDotRelatedAppointmentId: string | null; // todo: get rid of this after refactor to handle nav better
  editAppointmentInstance: AppointmentSchema | null; // The 'current' appointment that gets saved when you choose to edit the insurance @TODO: Kill appointments!!
  pcPurchasableProcedures: ProcedureSchema[];
  unscheduledPaymentType: InsuranceMode | AdditionalInsuranceMode;
  currentSlot: SlotsSchema;
  slots: SlotsSchema[];
  globalAppointments: AppointmentSchema[]; // Slots to be considered outside of the current appointment
  boughtTreatments: BoughtTreatmentSchema[];
  paymentIntent: PaymentIntent | undefined;
  appointmentStatuses: AppointmentStatusObject;
  patientViewDetails: boolean;
  currentSlotBeingEdited: SlotsSchema | null;
  changeCurrentSlot: (newData: Partial<SlotsSchema>) => void;
  addSlot: (slot: SlotsSchema | SlotsSchema[]) => void;
  addGlobalAppointments: (appointments: AppointmentSchema[]) => void;
  editSlot: (slot: SlotsSchema) => void;
  deleteSlot: (slot: SlotsSchema) => void;
  addBoughtTreatments: (
    boughtTreatment: BoughtTreatmentSchema | BoughtTreatmentSchema[],
  ) => void;
  addPcPurchasableProcedures: (
    procedures: ProcedureSchema | ProcedureSchema[],
  ) => void;
  setUnscheduledPaymentType: (
    paymentType: InsuranceMode | AdditionalInsuranceMode,
  ) => void;
  resetBoughtTreatments: () => void;
  setAppointments: (
    newData: Partial<AppointmentSchema> | Partial<AppointmentSchema>[],
  ) => void;
  setGlobalDotrelatedConsumerId: (arg: string | null) => void; // todo: get rid of this after refactor to handle nav better
  setGlobalDotRelatedAppointmentId: (arg: string | null) => void; // todo: get rid of this after refactor to handle nav better
  setEditAppointmentInstance: (arg: AppointmentSchema) => void;
  editAppointment: (newData: AppointmentSchema) => void;
  deleteAppointment: (appt: AppointmentSchema) => void;
  setPatientViewDetails: (arg: boolean) => void;
  editPcPurchasableProcedures: (newData: ProcedureSchema) => void;
  resetSlots: () => void;
  resetAppointment: () => void;
  resetClientFormState: () => void;
  resetGlobalAppointments: () => void;
  setAppointmentStatuses: (arg: AppointmentStatusObject) => void;
  setPaymentIntent: (newValue?: {
    paymentIntentId: string;
    clientSecret: string;
    customerOptions?: { customer: string; ephemeralKey: string };
  }) => void;
  setCurrentSlotBeingEdited: (arg: SlotsSchema | null) => void;
}

export const initialAppointmentsInfo: AppointmentSchema = {
  status: AppointmentStatusEnum.Upcoming,
  id: "",
  slots: [],
  appointmentDate: "",
  location: { id: "", label: "", locationName: "" },
  intakeFormIds: [],
  isIntakeComplete: false,
  intakeBypassed: false,
};

/**
 * Slots in the client is use to locate it for editing / deleting - and is not representative of slotId that gets generated
 */
const initialCurrentSlot: SlotsSchema = {
  id: "",
  slotStartTime: "",
  slotEndTime: "",
  location: {
    label: "",
    id: "",
    locationName: "",
    timezone: "",
  },
  procedures: [
    {
      id: 0,
      label: "",
      hrtId: 0,
      selectedPaymentType: "SELFPAY" as const,
      consumerId: "",
      price: 0,
    },
  ],
  provider: {
    label: "",
    id: "",
    image: "",
    firstName: "",
    lastName: "",
  },

  ehrVisitIds: [],
  customSlot: false,
  clientStatus: "NEW",
  status: SlotStatusEnum.Upcoming,
  slotPaymentType: InsuranceMode.INSURANCE,
  note: null,
  reviewStatus: null,
  orgLevelEhrSchedulingType: "DEFAULT", // Assuming this is the default scheduling type
  isDeleteAndCreate: false, // Flag to indicate if this is a delete and create operation
};

export const createAppointmentsSlice: StateCreator<
  Store,
  [["zustand/devtools", never], ["zustand/persist", unknown]],
  [],
  AppointmentSlice
> = (set, get) => ({
  appointments: [],
  currentSlotBeingEdited: null,
  globalDotRelatedConsumerId: null,
  globalDotRelatedAppointmentId: null, // todo: get rid of this after refactor to handle nav better
  editAppointmentInstance: null,
  slots: [],
  globalAppointments: [],
  pcPurchasableProcedures: [],
  unscheduledPaymentType: InsuranceMode.SELFPAY,
  paymentIntent: undefined,
  currentSlot: initialCurrentSlot,
  boughtTreatments: [],
  appointmentStatuses: {},
  patientViewDetails: false,
  setPatientViewDetails: (arg: boolean) => {
    set((state) => ({
      patientViewDetails: arg,
    }));
  },
  changeCurrentSlot: (newData) =>
    set((state) => ({ currentSlot: { ...state.currentSlot, ...newData } })),
  addBoughtTreatments: (newBoughtTreatment) => {
    if (Array.isArray(newBoughtTreatment)) {
      set((state) => ({
        boughtTreatments: [...state.boughtTreatments, ...newBoughtTreatment],
      }));
    } else {
      set((state) => ({
        boughtTreatments: [...state.boughtTreatments, newBoughtTreatment],
      }));
    }
  },
  addSlot: (newSlot) => {
    if (Array.isArray(newSlot)) {
      set((state) => ({ slots: [...state.slots, ...newSlot] }));
    } else {
      set((state) => ({ slots: [...state.slots, newSlot] }));
    }
  },
  addPcPurchasableProcedures: (procedures) => {
    if (Array.isArray(procedures)) {
      set((state) => ({
        pcPurchasableProcedures: [
          ...state.pcPurchasableProcedures,
          ...procedures,
        ],
      }));
    } else {
      set((state) => ({
        pcPurchasableProcedures: [...state.pcPurchasableProcedures, procedures],
      }));
    }
  },
  setUnscheduledPaymentType: (paymentType) => {
    set((state) => ({
      unscheduledPaymentType: paymentType,
    }));
  },
  editPcPurchasableProcedures: (procedure) =>
    set((state) => ({
      pcPurchasableProcedures: state.pcPurchasableProcedures.map((obj) =>
        obj.clientSideProcedureId === procedure.clientSideProcedureId
          ? procedure
          : obj,
      ),
    })),
  addGlobalAppointments: (allAppointments) => {
    set((state) => ({ globalAppointments: allAppointments }));
  },
  setEditAppointmentInstance: (appointment) => {
    set((state) => ({ editAppointmentInstance: appointment }));
  },
  setCurrentSlotBeingEdited: (slot) => {
    set((state) => ({ currentSlotBeingEdited: slot }));
  },
  editSlot: (slot) =>
    set((state) => ({
      slots: state.slots.map((obj) => (obj.id === slot.id ? slot : obj)),
    })),
  deleteSlot: (slot) => {
    // If deleting a slot that has already been created, don't remove it immediately, instead delete
    // it on the last step so we can remove its charges / bought treatments
    // If the slot deletion is triggered when is already has the DELETED status, it means the undo button is
    // clicked ( otherwise it would not have existed in the first place )
    if (slot.clientStatus === "EDITING") {
      slot.clientStatus = "DELETED";

      const isEveryTreatmentSlotDeleted = slot.procedures.every(
        (procedure) => procedure.procedureClientStatus === "DELETED",
      );

      const slotsWithRemovedTreatment = {
        ...slot,

        procedures: slot.procedures
          .filter((procedure) => {
            if (procedure.clientSideProcedureId) {
              return false;
            }
            return true;
          })
          .map((procedure) => {
            if (!procedure.boughtTreatmentId) return procedure;

            const editedProcedure = {
              ...procedure,
              ...(isEveryTreatmentSlotDeleted
                ? {}
                : {
                    procedureClientStatus: getProcedureClientStatus(
                      procedure.procedureClientStatus,
                    ),
                  }),
            };

            return editedProcedure;
          }),
      };

      get().editSlot(slotsWithRemovedTreatment);
    } else if (slot.clientStatus === "NEW") {
      set((state) => ({
        slots: state.slots.filter((obj) => obj.id !== slot.id),
      }));
    } else if (slot.clientStatus === "DELETED") {
      /**
       * Undo
       */
      slot.clientStatus = "EDITING";
      const slotsWithRemovedTreatment = {
        ...slot,
        cancellation: undefined,
        procedures: slot.procedures.map((procedure) => {
          if (!procedure.boughtTreatmentId) return procedure;

          const editedProcedure = {
            ...procedure,
            procedureClientStatus: getProcedureClientStatus(
              procedure.procedureClientStatus,
            ),
          };

          return editedProcedure;
        }),
      };

      get().editSlot(slotsWithRemovedTreatment);
    }
  },
  resetAppointment: () => {
    set((state) => ({
      pcPurchasableProcedures: [],
      appointmentStatuses: {},
      appointments: [],
    }));
  },
  resetGlobalAppointments: () =>
    set((state) => ({
      globalAppointments: [],
      editAppointmentInstance: null,
    })),
  resetSlots: () => {
    set((state) => ({
      slots: [],
      globalSlots: [],
    }));
  },
  resetBoughtTreatments: () => set((state) => ({ boughtTreatments: [] })),
  resetClientFormState: () => {
    get().resetGlobalAppointments();
    get().resetAppointment();
    get().resetSlots();
    get().resetBoughtTreatments();
  },
  setAppointments: (newData) => {
    if (Array.isArray(newData)) {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      set((state: any) => ({
        ...state,
        appointments: [...state.appointments, ...newData],
      }));
    } else {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      set((state: any) => ({
        ...state,
        appointments: [...state.appointments, newData],
      }));
    }
  },
  // todo: get rid of this after refactor to handle nav better
  setGlobalDotrelatedConsumerId: (id) => {
    set((state: AppointmentSlice) => ({
      ...state,
      globalDotRelatedConsumerId: id,
    }));
  },
  // todo: get rid of this after refactor to handle nav better
  setGlobalDotRelatedAppointmentId: (id) => {
    set((state: AppointmentSlice) => ({
      ...state,
      globalDotRelatedAppointmentId: id,
    }));
  },
  setAppointmentStatuses: (appointmentStatuses: {
    [id: string]: {
      isUpdated: boolean;
      updatedSlots: string[];
      reason: string;
    };
  }) => {
    set((state) => ({
      ...state,
      appointmentStatuses: appointmentStatuses,
    }));
  },

  editAppointment: (appointment) =>
    set((state) => ({
      ...state,
      appointments: state.appointments.map((obj) =>
        obj.id === appointment.id ? appointment : obj,
      ),
    })),
  deleteAppointment: (appointments) => {
    set((state) => ({
      appointments: state.appointments.filter(
        (obj) => obj.id !== appointments.id,
      ),
    }));
  },
  setPaymentIntent: (newValue) => set({ paymentIntent: newValue }),
});
